import { isMajorCardId } from '#lib/domain/cards.ts';
import { GameStage, type MajorCardId, type MinorCardId } from '#lib/domain/types.ts';
import type { MajorCardState, MinorCardState } from '../domain/game.state.validation';
import type { GameRepository } from './game-repository.port';
import { MajorArcanaState, type MajorCardsView } from './major-cards.state.svelte';
import { MinorArcanaState, type MinorCardsView } from './minor-cards.state.svelte';

export type GameStateDTO = {
	hasRetrievedFromStorage: boolean;
	gameStage: GameStage;
	majorCards: MajorCardState;
	minorCards: MinorCardState;
};

export class GameState {
	private stage = $state(GameStage.CHOOSE_INITIAL_DEITY);
	private retrievedFromStorage = $state(false);
	private readonly majorArcana = new MajorArcanaState();
	private readonly minorArcana = new MinorArcanaState();

	constructor(private readonly storage: GameRepository) {}

	public static get initialState(): GameStateDTO {
		return {
			hasRetrievedFromStorage: false,
			gameStage: GameStage.CHOOSE_INITIAL_DEITY,
			majorCards: MajorArcanaState.initialState,
			minorCards: MinorArcanaState.initialState
		};
	}

	get initialState() {
		return GameState.initialState;
	}

	get gameStage() {
		return this.stage;
	}

	get hasRetrievedFromStorage() {
		return this.retrievedFromStorage;
	}

	get majorCards(): MajorCardsView {
		return this.majorArcana;
	}

	get minorCards(): MinorCardsView {
		return this.minorArcana;
	}

	get mayDrawMajorCard() {
		return this.stage === GameStage.MAIN_GAME && this.majorArcana.mayDrawCard;
	}

	get mayDrawMinorCard() {
		return this.stage === GameStage.MAIN_GAME && this.minorArcana.mayDrawCard;
	}

	get maySaveMajorCard() {
		return (
			this.stage === GameStage.MAIN_GAME &&
			!this.majorArcana.hasSavedThisRound &&
			!this.majorArcana.hasMaxSavedCards &&
			this.minorArcana.hasMaxFlippedCards
		);
	}

	get mayClearSpread() {
		return this.stage === GameStage.MAIN_GAME && this.minorArcana.mayClearSpread;
	}

	maySaveMinorCard = (cardId: MinorCardId): boolean => {
		return (
			this.stage === GameStage.MAIN_GAME &&
			this.minorArcana.isCardDrawn(cardId) &&
			this.minorArcana.maySaveCard(cardId)
		);
	};

	onSaveInitialDeity = (cardId: MajorCardId) => {
		if (this.stage !== GameStage.CHOOSE_INITIAL_DEITY || !isMajorCardId(cardId)) return;

		this.majorArcana.saveInitialDeity(cardId);
		this.stage = GameStage.MAIN_GAME;
	};

	drawMajorCard = () => {
		if (!this.mayDrawMajorCard) return;
		this.majorArcana.draw();
	};

	saveMajorCard = (cardId: MajorCardId) => {
		if (!this.maySaveMajorCard || !this.majorArcana.hand.includes(cardId)) return;
		this.majorArcana.save(cardId);
	};

	reverseMajorCard = (cardId: MajorCardId) => {
		if (
			this.stage !== GameStage.MAIN_GAME ||
			!this.majorArcana.hand.includes(cardId) ||
			this.majorArcana.isCardReversed(cardId)
		)
			return;

		this.majorArcana.reverse(cardId);
	};

	dismissMajorCard = (cardId: MajorCardId) => {
		if (this.stage !== GameStage.MAIN_GAME || !this.majorArcana.hand.includes(cardId)) return;
		this.majorArcana.discard(cardId);
	};

	drawMinorCard = () => {
		if (!this.mayDrawMinorCard) return;
		this.minorArcana.draw();
	};

	flipMinorCard = (cardId: MinorCardId) => {
		if (
			this.stage !== GameStage.MAIN_GAME ||
			!this.minorArcana.isCardDrawn(cardId) ||
			this.minorArcana.isCardFlipped(cardId)
		)
			return;

		this.minorArcana.flip(cardId);
	};

	saveMinorCard = (cardId: MinorCardId) => {
		if (!this.maySaveMinorCard(cardId)) return;
		this.minorArcana.save(cardId);
	};

	onClearSpread = () => {
		if (!this.mayClearSpread) return;
		this.minorArcana.clearSpread();
		this.majorArcana.resetSaveStatus();
	};

	toggleFinaleDeck = () => {
		if (this.stage !== GameStage.MAIN_GAME) return;
		this.minorArcana.toggleFinaleDeck();
	};

	reset = () => {
		this.storage.clear();
		this.stage = GameStage.CHOOSE_INITIAL_DEITY;
		this.retrievedFromStorage = false;
		this.majorArcana.reset();
		this.minorArcana.reset();
	};

	private loadFromStorage() {
		const savedState = this.storage.restore();
		this.setGameState({
			...(savedState || GameState.initialState),
			hasRetrievedFromStorage: true
		});
	}

	private setGameState(state: GameStateDTO) {
		this.stage = state.gameStage;
		this.retrievedFromStorage = state.hasRetrievedFromStorage;
		this.majorArcana.restoreState(state.majorCards);
		this.minorArcana.restoreState(state.minorCards);
	}

	toDTO = (): GameStateDTO => {
		return {
			hasRetrievedFromStorage: this.hasRetrievedFromStorage,
			gameStage: this.gameStage,
			majorCards: this.majorArcana.toDTO(),
			minorCards: this.minorArcana.toDTO()
		};
	};

	public get isReady() {
		return this.storage.storageReady();
	}

	public init() {
		if (!this.hasRetrievedFromStorage && this.isReady) {
			this.loadFromStorage();
		}
	}

	public saveCurrentState() {
		if (this.isReady) {
			this.storage.save(this.toDTO());
		}
	}
}
